import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "500px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const toNumber = (v) => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toLatLng = (obj) => {
  if (!obj) return null;
  const lat = toNumber(obj.lat ?? obj.latitude);
  const lng = toNumber(obj.lng ?? obj.longitude);
  if (lat == null || lng == null) return null;
  return { lat, lng };
};

const RideSharePassengerInTransitRouteTrack = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  const mapRef = useRef(null);

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [midpoints, setMidpoints] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);

  const [directions, setDirections] = useState(null);
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(true); // Start with legend collapsed

  const center = useMemo(() => {
    // Always center on driver location if available
    if (currentLocation) {
      return currentLocation;
    }
    
    // Fallback to origin if no driver location
    if (origin) {
      return origin;
    }
    
    // Final fallback to default center
    return defaultCenter;
  }, [currentLocation, origin]);

  const zoom = useMemo(() => {
    const points = [
      origin,
      ...(pickup ? [pickup] : []),
      ...midpoints,
      ...(dropoff ? [dropoff] : []),
      destination,
      ...(currentLocation ? [currentLocation] : [])
    ].filter(Boolean);

    if (points.length < 2) return 14; // Increased default zoom for better visibility

    // Calculate max distance between points to determine zoom
    let maxDistance = 0;
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const distance = Math.sqrt(
          Math.pow(points[i].lat - points[j].lat, 2) + 
          Math.pow(points[i].lng - points[j].lng, 2)
        );
        maxDistance = Math.max(maxDistance, distance);
      }
    }

    // Adjust zoom based on max distance - more zoomed out for small screens
    if (maxDistance > 0.5) return 9;  // Very far apart
    if (maxDistance > 0.2) return 11; // Far apart  
    if (maxDistance > 0.1) return 12; // Medium distance
    if (maxDistance > 0.05) return 13; // Close
    return 14; // Very close - increased from 13
  }, [origin, pickup, destination, dropoff, midpoints, currentLocation]);

  useEffect(() => {
    const handleMessage = (event) => {
      try {
        const raw = event?.data;
        if (!raw) return;
        const payload = typeof raw === "string" ? JSON.parse(raw) : raw;

        const trip = payload?.trip ?? payload?.tripDetails ?? payload;
        const req = payload?.passengerRequest ?? payload?.request ?? null;

        const o = toLatLng({ lat: trip?.origin_lat, lng: trip?.origin_lng });
        const d = toLatLng({ lat: trip?.destination_lat, lng: trip?.destination_lng });
        const p = req ? toLatLng({ lat: req?.pickup_lat, lng: req?.pickup_lng }) : null;
        const doff = req ? toLatLng({ lat: req?.dropoff_lat, lng: req?.dropoff_lng }) : null;

        const mids = Array.isArray(payload?.midpoints)
          ? payload.midpoints
          : Array.isArray(trip?.midpoints)
            ? trip.midpoints
            : [];

        const midPts = (Array.isArray(mids) ? mids : [])
          .map((m) => toLatLng({ lat: m?.lat ?? m?.latitude, lng: m?.lng ?? m?.longitude }))
          .filter(Boolean);

        const cur = payload?.currentLocation
          ? toLatLng(payload.currentLocation)
          : payload?.driverLocation
            ? toLatLng(payload.driverLocation)
            : null;

        if (o) setOrigin(o);
        if (d) setDestination(d);
        setPickup(p);
        setDropoff(doff);
        setMidpoints(midPts);
        if (cur) setCurrentLocation(cur);
      } catch (e) {
        return;
      }
    };

    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google) return;
    if (!origin || !destination) return;

    const points = [
      origin,
      ...(pickup ? [pickup] : []),
      ...midpoints,
      ...(dropoff ? [dropoff] : []),
      destination,
    ].filter(Boolean);

    const limited = points.slice(0, 10);
    if (limited.length < 2) return;

    const service = new window.google.maps.DirectionsService();
    const o = limited[0];
    const d = limited[limited.length - 1];
    const waypoints = limited.slice(1, limited.length - 1).map((p) => ({
      location: p,
      stopover: true,
    }));

    service.route(
      {
        origin: o,
        destination: d,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          setDirections(null);
        }
      }
    );
  }, [isLoaded, origin, destination, pickup, dropoff, midpoints]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <style>
        {`
          .custom-marker-label {
            background: rgba(0, 0, 0, 0.8) !important;
            color: white !important;
            padding: 6px 12px !important;
            border-radius: 20px !important;
            font-size: 14px !important;
            font-weight: bold !important;
            white-space: nowrap !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
            border: 2px solid rgba(255, 255, 255, 0.3) !important;
            text-align: center !important;
            min-width: 80px !important;
            backdrop-filter: blur(4px) !important;
          }
          .driver-marker-label {
            background: rgba(255, 215, 0, 0.9) !important;
            color: #1976D2 !important;
            padding: 6px 12px !important;
            border-radius: 20px !important;
            font-size: 14px !important;
            font-weight: bold !important;
            white-space: nowrap !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
            border: 2px solid #1976D2 !important;
            text-align: center !important;
            min-width: 80px !important;
            backdrop-filter: blur(4px) !important;
          }
          .map-legend {
            background: rgba(255, 255, 255, 0.95) !important;
            border: 1px solid #ddd !important;
            border-radius: 8px !important;
            padding: 12px !important;
            margin: 10px 0 !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
            position: fixed !important;
            top: 20px !important;
            right: 20px !important;
            z-index: 1000 !important;
            max-width: 250px !important;
            min-width: 200px !important;
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          .map-legend.collapsed {
            transform: translateX(270px) !important;
          }
          .legend-toggle {
            background: #1976D2 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            width: 40px !important;
            height: 40px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            cursor: pointer !important;
            font-size: 18px !important;
            font-weight: bold !important;
            box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3) !important;
            transition: all 0.2s ease !important;
            position: absolute !important;
            left: -50px !important;
            top: 0 !important;
          }
          .legend-toggle:hover {
            background: #1565C0 !important;
            transform: scale(1.05) !important;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4) !important;
          }
          .legend-toggle:active {
            transform: scale(0.95) !important;
          }
          .legend-content {
            opacity: 1 !important;
            transition: opacity 0.3s ease !important;
          }
          .map-legend.collapsed .legend-content {
            opacity: 0 !important;
            pointer-events: none !important;
          }
          .legend-title {
            font-size: 16px !important;
            font-weight: bold !important;
            margin-bottom: 8px !important;
            color: #333 !important;
          }
          .legend-items {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 12px !important;
          }
          .legend-item {
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
          }
          .legend-icon {
            width: 20px !important;
            height: 20px !important;
            border-radius: 50% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 10px !important;
            font-weight: bold !important;
            color: white !important;
          }
          .legend-text {
            font-size: 12px !important;
            color: #555 !important;
          }
        `}
      </style>
      <h2>Passenger In-Transit Tracking</h2>
      
      {/* Map Legend */}
      <div className={`map-legend ${isLegendCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="legend-toggle" 
          onClick={() => setIsLegendCollapsed(!isLegendCollapsed)}
          title={isLegendCollapsed ? "Show Legend" : "Hide Legend"}
        >
          {isLegendCollapsed ? '◀' : '▶'}
        </button>
        
        <div className="legend-content">
          <div className="legend-title">📍 Map Legend</div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#2E7D32" }}>●</div>
              <div className="legend-text">START - Trip Beginning</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#1976D2" }}>●</div>
              <div className="legend-text">PICKUP - Your Location</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#F57C00" }}>●</div>
              <div className="legend-text">DROPOFF - Exit Point</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#7B1FA2" }}>●</div>
              <div className="legend-text">STOPS - Intermediate Points</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#C62828" }}>●</div>
              <div className="legend-text">DESTINATION - Final Point</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#FFD700", border: "2px solid #000000", color: "#000000" }}>●</div>
              <div className="legend-text">DRIVER - Current Location</div>
            </div>
          </div>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={zoom}
        center={center}
        onLoad={(map) => (mapRef.current = map)}
        options={{
          gestureHandling: "auto",
          scrollwheel: true,
          zoomControl: true,
          panControl: true,
          scaleControl: true,
        }}
      >
        {origin && (
          <Marker 
            position={origin} 
            title="Trip Starting Point"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#2E7D32",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        )}
        {pickup && (
          <Marker 
            position={pickup} 
            title="Your Pickup Location"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#1976D2",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        )}
        {dropoff && (
          <Marker 
            position={dropoff} 
            title="Your Dropoff Location"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#F57C00",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        )}
        {midpoints.map((m, idx) => (
          <Marker 
            key={`mid-${idx}`} 
            position={m} 
            title={`Stop ${idx + 1} on Route`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: "#7B1FA2",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        ))}
        {destination && (
          <Marker 
            position={destination} 
            title="Trip Destination"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#C62828",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        )}
        {currentLocation && (
          <Marker 
            position={currentLocation} 
            title="Driver Current Location" 
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#FFD700",
              fillOpacity: 1.0,
              strokeColor: "#000000",
              strokeWeight: 3,
            }}
          />
        )}

        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: { strokeColor: "#7B1FA2", strokeWeight: 5, strokeOpacity: 0.85 },
              preserveViewport: true,
              draggable: false,
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default RideSharePassengerInTransitRouteTrack;
